'use client';

import { useMemo } from 'react';

import { useCheckoutStore } from '@/stores';
import type { PromotionOffer } from '@/utils';

type ExtendedPurchaseData = {
  raffleId?: string;
  ticketsAmount?: number;
  totalValue?: number;
  raffleName?: string;
  ticketPrice?: number;
  prizeValue?: number;
  drawDate?: string;
  isPromotionAvailable?: boolean;
  promotionPercentage?: number;
  promotionTicketsAmount?: number;
  promotionTitle?: string;
  promotionDescription?: string;
  maxTicketsPerPurchase?: number;
  promotionOffers?: PromotionOffer[];
};

export const useCheckoutData = () => {
  const { checkoutData } = useCheckoutStore();

  const purchaseData = useMemo(() => {
    return checkoutData.purchaseData as ExtendedPurchaseData | null;
  }, [checkoutData.purchaseData]);

  const purchaseDataFields = useMemo(() => {
    return {
      raffleId: purchaseData?.raffleId,
      ticketsAmount: purchaseData?.ticketsAmount,
      totalValue: purchaseData?.totalValue,
      raffleName: purchaseData?.raffleName,
      ticketPrice: purchaseData?.ticketPrice,
      prizeValue: purchaseData?.prizeValue,
      drawDate: purchaseData?.drawDate,
      isPromotionAvailable: purchaseData?.isPromotionAvailable,
      promotionPercentage: purchaseData?.promotionPercentage,
      promotionTicketsAmount: purchaseData?.promotionTicketsAmount,
      promotionTitle: purchaseData?.promotionTitle,
      promotionDescription: purchaseData?.promotionDescription,
      maxTicketsPerPurchase: purchaseData?.maxTicketsPerPurchase,
      promotionOffers: purchaseData?.promotionOffers,
    };
  }, [purchaseData]);

  const formattedData = useMemo(() => {
    const { drawDate, prizeValue, totalValue, ticketPrice } = purchaseDataFields;

    return {
      formattedDrawDate: drawDate ? new Date(drawDate).toLocaleDateString('pt-BR') : null,
      formattedPrizeValue: prizeValue
        ? `R$ ${prizeValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        : null,
      formattedTotalValue: totalValue
        ? `R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        : null,
      formattedTicketPrice: ticketPrice
        ? `R$ ${ticketPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
        : null,
    };
  }, [purchaseDataFields]);

  const states = useMemo(() => {
    const { raffleId, ticketsAmount, totalValue, raffleName } = purchaseDataFields;

    return {
      hasPurchaseData: Boolean(purchaseData),
      hasValidPurchaseData: Boolean(
        raffleId && ticketsAmount && totalValue && raffleName,
      ),
      isFormStep: true,
      isPaymentStep: false,
      isCompletedStep: false,
    };
  }, [purchaseData, purchaseDataFields]);

  return {
    purchaseData,
    ...purchaseDataFields,
    ...formattedData,
    ...states,
  };
};
